<script setup lang="ts">
import {computed, inject, ref} from "vue";
import type {Dayjs} from "dayjs";
import {Icon, Button} from "@/Components/Library";
import type {AiChatMessage} from "./ai-conversation-types";
import User from "@/Models/User";
import markdownit from "markdown-it";
import {useToast} from "primevue/usetoast";

const $date = inject("date") as (date?: Date | string) => Dayjs;
const $user = new User();
const toast = useToast();
const md = markdownit();

const {message, ind, isSameDate} = defineProps<{message: AiChatMessage; ind: number; isSameDate: boolean}>();

const messageBody = ref<HTMLElement | null>(null);

const isUserMessage = computed(() => message.role === "user");
const isAiMessage = computed(() => message.role === "ai");

const copyMessage = () => {
    if (messageBody.value?.textContent) {
        navigator.clipboard.writeText(messageBody.value.textContent);
        toast.add({
            severity: "success",
            summary: "Copied",
            detail: "Message copied to clipboard",
            life: 3000,
        });
    }
};
</script>

<template>
    <div>
        <!-- Date Separator -->
        <div v-if="!isSameDate" class="flex items-center justify-center pb-8 pt-8 text-stone-500 dark:text-stone-400">
            <hr class="grow border-stone-300 dark:border-stone-800" />
            <span class="mx-6 text-sm font-semibold">
                <small>{{ $date(message.created_at).format("ddd Do MMM YYYY") }}</small>
            </span>
            <hr class="grow border-stone-300 dark:border-stone-800" />
        </div>

        <!-- Message -->
        <div class="mb-4 flex items-start gap-3" :class="[isUserMessage ? 'justify-end' : '']">
            <!-- Avatar -->
            <div class="flex h-8 w-8 shrink-0 items-center justify-center">
                <Icon v-if="isAiMessage" icon="ri:robot-2-fill" :width="32" :height="32" class="text-askr-green-500" />
            </div>

            <!-- Message Bubble -->
            <div
                class="relative w-full max-w-xl rounded-md border px-4 py-3 dark:border-stone-800"
                :class="[
                    isUserMessage
                        ? 'border-stone-300 bg-stone-100 dark:bg-stone-800'
                        : 'border-stone-200 bg-white dark:bg-stone-900',
                ]"
            >
                <!-- Role Label -->
                <div
                    class="mb-2 text-xs font-semibold"
                    :class="[
                        isAiMessage
                            ? 'text-askr-green-600 dark:text-askr-green-400'
                            : 'text-stone-600 dark:text-stone-400',
                    ]"
                >
                    {{ isAiMessage ? "Albie" : "You" }}
                </div>

                <!-- Action Buttons -->
                <div class="absolute right-1 top-2">
                    <Button
                        class="!p-1 hover:!bg-stone-200 dark:hover:!bg-stone-700"
                        :icon="{icon: 'prime:copy', width: '20'}"
                        @click="copyMessage"
                    />
                </div>

                <!-- Message Text -->
                <div
                    class="prose prose-sm prose-stone mb-2 break-words dark:prose-invert"
                    v-html="md.render(message.text)"
                    ref="messageBody"
                ></div>

                <!-- Footer -->
                <div class="flex items-center justify-end gap-2">
                    <div class="text-xs font-semibold text-stone-500 dark:text-stone-400">
                        {{ $date(message.created_at).format("HH:mm") }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
